import styled from 'styled-components';

import Menu from 'components/shared/menu';
import { BodySmall } from 'styles/typography';
import CheckboxOffIcon from 'svg/CheckboxOffIcon';
import CheckboxOnIcon from 'svg/CheckboxOnIcon';
import TableIcon from 'svg/TableIcon';

export type ToggleColumn = {
  id: string;
  label: string;
  visible: boolean;
  locked?: boolean;
};

type Props = {
  columns: ToggleColumn[];
  onToggle: (id: string) => void;
};

export default function ColumnToggle({ columns, onToggle }: Props) {
  const hiddenCount = columns.filter((c) => !c.visible).length;

  return (
    <Menu $size='small'>
      <Menu.Trigger>
        <Trigger title='Edit columns'>
          <TableIcon width={14} height={14} />
          {hiddenCount > 0 && <HiddenLabel>{hiddenCount} hidden</HiddenLabel>}
        </Trigger>
      </Menu.Trigger>
      <Menu.Content position='right'>
        {columns.map((col) => (
          <Row
            key={col.id}
            $locked={col.locked}
            onClick={(e) => {
              // Stop the click from reaching Menu's useClickOutside listener so the
              // dropdown stays open across multiple toggles.
              e.stopPropagation();
              if (col.locked) return;
              onToggle(col.id);
            }}>
            {col.visible ? <CheckboxOnIcon width={14} height={14} /> : <CheckboxOffIcon width={14} height={14} />}
            <BodySmall>{col.label}</BodySmall>
          </Row>
        ))}
      </Menu.Content>
    </Menu>
  );
}

const Trigger = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.black50};
  cursor: pointer;
  display: flex;
  gap: 4px;
  justify-content: center;
  padding: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.blue10};
    color: ${({ theme }) => theme.colors.blue100};
  }
`;

const HiddenLabel = styled.span`
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
`;

const Row = styled.div<{ $locked?: boolean }>`
  align-items: center;
  cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
  display: flex;
  gap: 8px;
  opacity: ${({ $locked }) => ($locked ? 0.5 : 1)};
  padding: 8px 12px;

  & > svg {
    color: ${({ theme }) => theme.colors.blue100};
  }

  &:hover {
    background-color: ${({ $locked, theme }) => ($locked ? 'transparent' : theme.colors.blue10)};
  }
`;
